import React, { useMemo, useState, useEffect } from "react";
import { connect } from "react-redux";
import { withI18n } from "@lingui/react";
import { Trans, t } from "@lingui/macro";
import { useMediaQuery } from "beautiful-react-hooks";
import isSSR from "../../constants/env/ssr";
import { availableLangs } from "../../functions/urlLanguage";
import { availableCurs } from "../../functions/currency";
import classNames from "../../functions/classNames";
import reverseUrl from "../../functions/reverseUrl";
import useCurrentLanguage from "../../functions/languages/useCurrentLanguage";
import { getBaseUrl } from "../../functions/getBaseUrl";
import useRemoteConfig from "../../functions/useRemoteConfig";
import { Icon } from "../Icon";
import Search from "../Search";
import Button from "../Button";
import sendEvent from "../../functions/analytics";
import UserAccount from "./UserAccount";
import CurrencyMenu from "./CurrencyMenu";
import HelpMenu from "./HelpMenu";
import LanguageMenu from "./LanguageMenu";
import "./Header.css";

const CreateTourButton = ({ isMobile, onClick }) => (
  <div className="button--create-tour">
    <Button
      icon={<Icon name="magic-stars" />}
      className="Header__button Header__button--create-tour"
      theme="accent-full"
      size={isMobile ? "M" : "S"}
      aria-label={isMobile ? "Создать тур" : undefined}
      onClick={onClick}
    >
      {!isMobile && <Trans>Create Tour</Trans>}
    </Button>
  </div>
);

const GetSubscriptionButton = ({ isMobile, onClick }) => (
  <div className="button--create-tour">
    <Button
      icon={<Icon name="magic-stars" />}
      className="Header__button Header__button--create-tour"
      theme="accent-full"
      size={isMobile ? "M" : "S"}
      aria-label="Get Subscription"
      onClick={onClick}
    >
      {!isMobile && <Trans>Get Subscription</Trans>}
    </Button>
  </div>
);

/**
 * Header for pages
 * @param {Object} $ - component props
 * @param {Object} $.user - current user
 * @param {Boolean} $.sticky - sticky CSS position
 * @param {Boolean} $.showSearch - display searchbar or not
 * @param {Boolean} $.language - to force language if needed
 */
const Header = withI18n()(
  ({
    sticky,
    showSearch,
    showHelp,
    minimal,
    language,
    i18n,
    currentCurrency: currency,
    isMainPage,
  }) => {
    const lang = useCurrentLanguage();
    const locale = language || lang;
    const [screen, setScreen] = useState(null);
    const [Dropdown, setDropdown] = useState(null);
    const [isScrolled, setIsScrolled] = useState(false);
    const isMobile = !isSSR && useMediaQuery("(max-width: 767px)");
    const isDesktop = !isSSR && useMediaQuery("(min-width: 992px)");
    const { hideCreateTourInHeader, showSubscriptionInHeader } = useRemoteConfig();
    let content = null;

    const isLangMenu = availableLangs.length > 1;
    // Don't show currency meny if there is no currentCurrency
    const isCurrMenu = availableCurs.length > 1 && currency;

    useEffect(() => {
      if (sticky) {
        const handleScroll = () => {
          setIsScrolled(window.scrollY > 0);
        };

        window.addEventListener("scroll", handleScroll);
        return () => window.removeEventListener("scroll", handleScroll);
      }
    }, [sticky]);

    /**
     * Sets menu from outer UserAccount component
     * @param {ReactComponent?} Component - what to put into menu, if null no array will be put into Dropdown state
     */
    const setDropdownOuter = Component => setDropdown(Component ? ["account", Component] : null);

    const redirectUrl = new URL("/generate-your-tour", getBaseUrl());

    const handleOnClick = () => {
      sendEvent("track", "on_demand_store_header_button_clicked");
      window.open(redirectUrl.toString(), "_blank");
    };

    const subscriptionOnClick = () => {
      const url = "https://wegotrip.com/club?utm_source=header";
      window.open(url, "_blank");
    };

    const helpMenu = useMemo(
      () => (
        <HelpMenu
          isMobile={isMobile}
          locale={locale}
          i18n={i18n}
          isDesktop={isDesktop}
          isMainPage={isMainPage}
        />
      ),
      [i18n, isDesktop, isMobile, locale, isMainPage],
    );

    switch (screen) {
      case "search":
        content = (
          <>
            <Search
              themes={["inline", "xs-fullsize"]}
              placeholder={i18n._(t`City, attraction, tour name...`)}
            />
            <div className="col-xs Header__button Header__button--close">
              <Button
                hideTextOnTablet
                hideTextOnMobile
                theme="simple"
                iconPosition="before"
                icon={<Icon name="close/white" />}
                onClick={() => setScreen(null)}
              />
            </div>
          </>
        );
        break;
      default:
        content = (
          <div className={classNames("row between-xs middle-xs", "Header__content")}>
            <div className="col-xs-3 col-md-2 col-lg-2 Header__logo-container">
              <a
                href={reverseUrl("main", { lang: locale })}
                aria-label={
                  locale === "ru" ? "На главную страницу WeGoTrip" : "Go to WeGoTrip homepage"
                }
                onClick={() => {
                  sendEvent("track", "click_on_logo_to_main");
                }}
              >
                <div
                  className="Header__logo"
                  role="img"
                  aria-label={locale === "ru" ? "Логотип WeGoTrip" : "WeGoTrip Logo"}
                />
              </a>
            </div>
            {showSearch ? (
              <div className="box Header__search">
                <Search
                  themes={["inline", "stroked", "werewolf"]}
                  placeholder={i18n._(t`City, attraction, tour name...`)}
                />
              </div>
            ) : null}
            <div
              className={classNames(
                "row end-xs middle-xs Header__buttons",
                !isMainPage && "Header__button_white",
              )}
            >
              {minimal && showHelp && helpMenu}
              {!minimal && (
                <>
                  {isLangMenu && (
                    <LanguageMenu
                      isMobile={isMobile}
                      locale={locale}
                      i18n={i18n}
                      availableLangs={availableLangs}
                    />
                  )}
                  {showSubscriptionInHeader && lang !== "ru" && (
                    <GetSubscriptionButton isMobile={isMobile} onClick={subscriptionOnClick} />
                  )}
                  {isMainPage && hideCreateTourInHeader && (
                    <CreateTourButton isMobile={isMobile} onClick={handleOnClick} />
                  )}
                  {isCurrMenu && (
                    <CurrencyMenu isMobile={isMobile} currency={currency} i18n={i18n} />
                  )}
                  <div>{helpMenu}</div>

                  {!isMobile && (
                    <a
                      href={reverseUrl("favorites", { lang: locale })}
                      className="Header__button Header__button--favourites"
                    >
                      <Button
                        hideTextOnTablet
                        hideTextOnMobile
                        theme="simple"
                        iconPosition="before"
                        icon={<Icon name="heart" width="20" height="20" />}
                      >
                        <Trans>Favorites</Trans>
                      </Button>
                    </a>
                  )}
                  <UserAccount
                    setDropdown={setDropdownOuter}
                    isMobile={isMobile}
                    isDesktop={isDesktop}
                    isMainPage={isMainPage}
                  />
                  {isMobile && !isMainPage && (
                    <Button
                      hideTextOnTablet
                      hideTextOnMobile
                      className="Header__button Header__button--search Header__button_white"
                      theme="simple"
                      iconPosition="before"
                      icon={<Icon name="search" />}
                      onClick={() => setScreen("search")}
                    />
                  )}
                </>
              )}
              {Dropdown && (
                <div className="col-xs Header__button Header__button--close-burger d-xs-only">
                  <Button
                    hideTextOnTablet
                    hideTextOnMobile
                    theme="simple"
                    iconPosition="before"
                    icon={<Icon name="close/white" />}
                    onClick={() => setDropdown(null)}
                  />
                </div>
              )}
            </div>
          </div>
        );
        break;
    }

    return (
      <header
        className={classNames(
          "Header",
          screen && `Header--${screen}`,
          sticky && "Header--sticky",
          sticky && isScrolled && "Header--sticky-scrolled",
          minimal && "Header--minimal",
          isMainPage && "Header--main-page",
          ...(Dropdown ? ["Header--menu", `Header--${Dropdown[0]}`] : []),
        )}
      >
        <div className="Wrapper Header__content-wrapper">
          {content}
          {Dropdown ? <div className="Header__menu d-xs-only">{Dropdown[1]}</div> : null}
        </div>
      </header>
    );
  },
);

/**
 * Copy redux state into component props
 * @param {Object} state - redux state
 */
function mapStateToProps(state) {
  return {
    user: state.user,
    currentCurrency: state.currencies.currentCurrency,
  };
}

export default connect(mapStateToProps)(Header);
